/**
 * @module NotificationItem
 * @package @geenius/notifications-solidjs
 * @description Renders a single inbox row for the Tailwind SolidJS variant.
 * The component keeps the primary read action on a real button while exposing
 * secondary link and dismiss actions separately for accessibility.
 */

import { motion } from '@geenius/motion/solidjs'
import type { Notification } from '@geenius/notifications/shared'
import {
  formatNotificationRelativeTime,
  getNotificationIcon,
} from '@geenius/notifications/shared'
import type { JSX } from 'solid-js'
import { Show } from 'solid-js'
import { createNotificationCopy } from '../lib/notificationCopy'
import {
  getNotificationAccentStyle,
  notificationIconFrameVariants,
} from '../lib/notificationStyles'
import { cn } from '../lib/utils'

/**
 * Props for a single notification row.
 */
export interface NotificationItemProps {
  notification: Notification
  onRead?: (id: string) => void
  onDismiss?: (id: string) => void
  class?: string
}

/**
 * Renders a single notification row with optional read and dismiss actions.
 *
 * @param props Notification row props.
 * @returns The rendered notification row.
 */
export function NotificationItem(props: NotificationItemProps): JSX.Element {
  const { t } = createNotificationCopy()
  const isUnread = () => props.notification.status === 'unread'

  return (
    <motion.article
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -1 }}
      transition={{ duration: 0.2 }}
      class={cn(
        'flex gap-4 px-5 py-4 transition-colors',
        isUnread()
          ? 'bg-[var(--gn-notifications-panel-surface)]'
          : 'bg-transparent hover:bg-[var(--gn-notifications-panel-surface-muted)]',
        props.class,
      )}
    >
      <Show
        when={props.notification.actorAvatar}
        fallback={
          <span
            class={notificationIconFrameVariants()}
            style={getNotificationAccentStyle(props.notification.type)}
          >
            {getNotificationIcon(props.notification.type)}
          </span>
        }
      >
        <img
          src={props.notification.actorAvatar}
          alt=""
          class="h-9 w-9 flex-shrink-0 rounded-full border border-border object-cover"
        />
      </Show>
      <div class="min-w-0 flex-1">
        <button
          type="button"
          onClick={() => {
            if (isUnread()) {
              props.onRead?.(props.notification.id)
            }
          }}
          disabled={!isUnread() || !props.onRead}
          aria-label={
            isUnread()
              ? t('notifications.item.markReadAria', {
                  title: props.notification.title,
                })
              : t('notifications.item.notificationAria', {
                  title: props.notification.title,
                })
          }
          class="w-full rounded-xl text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:cursor-default disabled:opacity-100"
        >
          <div class="flex items-start justify-between gap-2">
            <div>
              <div
                class={cn(
                  'text-[13px] leading-snug tracking-wide text-foreground',
                  isUnread() ? 'font-bold' : 'font-medium',
                )}
              >
                {props.notification.title}
              </div>
              <div class="mt-1 text-[11px] font-medium leading-snug text-muted-foreground">
                {props.notification.body}
              </div>
            </div>
            <Show when={isUnread()}>
              <span
                aria-hidden="true"
                class="mt-1 h-2 w-2 flex-shrink-0 rounded-full bg-primary shadow-sm"
              />
            </Show>
          </div>
        </button>
        <div class="mt-2 flex items-center gap-2">
          <span class="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
            {formatNotificationRelativeTime(props.notification.createdAt, t)}
          </span>
          <Show when={props.notification.actionUrl}>
            <span class="text-[10px] text-muted-foreground">•</span>
            <a
              href={props.notification.actionUrl}
              class="text-[10px] font-bold uppercase tracking-widest text-primary no-underline transition-colors hover:text-primary"
            >
              {props.notification.actionLabel ?? t('notifications.common.view')}
            </a>
          </Show>
          {props.onDismiss ? (
            <button
              type="button"
              onClick={() => props.onDismiss?.(props.notification.id)}
              class="ml-auto rounded-md px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
            >
              {t('notifications.common.dismiss')}
            </button>
          ) : null}
        </div>
      </div>
    </motion.article>
  )
}
